import { ChatApi } from '../api/chat-api';
import type {
  ChatAttachment,
  ChatFailedUpload,
  ChatUploadResult,
} from '../types/chat.types';

/**
 * ChatUploadUtil - I put one prompt's files on the server, in order. ONLY concerned with the
 * upload calls - nothing here knows about the transcript.
 */
export const ChatUploadUtil = {
  /**
   * DO NOT COVERT TO A PROMISE.ALL: the first upload sets the chatId that all
   * of the rest of the uploads must use.
   * */
  async uploadAll({
    attachments,
    chatId,
    mintChat,
  }: {
    attachments: ChatAttachment[];
    chatId: string | null;
    mintChat: (mintedChatId: string) => void;
  }): Promise<ChatUploadResult> {
    let sendChatId = chatId;
    let failedUpload: ChatFailedUpload | null = null;
    let error: Error | null = null;
    let fileIds: string[] = [];

    const stamped = [...attachments];

    try {
      fileIds = await attachments.reduce<Promise<string[]>>(
        async (earlier, attachment, index) => {
          const ids = await earlier;

          if (attachment.uploaded?.chatId === sendChatId) {
            return [...ids, attachment.uploaded.fileId];
          }

          const { data: file } = await ChatApi.uploadFile({
            chatId: sendChatId,
            file: attachment.file,
          }).catch(uploadError => {
            const failures = (attachment.uploadFailures ?? 0) + 1;

            stamped[index] = {
              file: attachment.file,
              id: attachment.id,
              uploaded: attachment.uploaded,
              uploadFailures: failures,
            };
            failedUpload = { failures, fileName: attachment.file.name };

            throw uploadError;
          });

          if (!sendChatId && file.chatId) {
            sendChatId = file.chatId;
            mintChat(file.chatId);
          }

          if (!file.fileId || !sendChatId) return ids;

          stamped[index] = {
            file: attachment.file,
            id: attachment.id,
            uploaded: { chatId: sendChatId, fileId: file.fileId },
            uploadFailures: attachment.uploadFailures,
          };

          return [...ids, file.fileId];
        },
        Promise.resolve([])
      );
    } catch (uploadError) {
      error =
        uploadError instanceof Error
          ? uploadError
          : new Error(String(uploadError));
    }

    return { chatId: sendChatId, error, failedUpload, fileIds, stamped };
  },
};
